import React, {useState} from 'react';
import {connect} from 'react-redux';
import AceEditor from 'react-ace';
import {FormRenderProps} from 'react-final-form';
import {Grid, Typography} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {Dispatch} from 'spectra-logic-ui';
import {updateResource} from 'spectra-logic-ui/actions';
import {Color} from 'spectra-logic-ui/colors';
import {FormDialog, TextField} from 'spectra-logic-ui/components';

import {validateName} from '@/validate';
import {IAMGroup, IAMPolicy} from '@/iam/types';

type Props = {
  group: IAMGroup;
  policy?: IAMPolicy;
  policies?: IAMPolicy[];
  onSubmit: (values: any) => Promise<any>;
};

export type FormData = {
  name: string;
  document: object;
}

const useStyles = makeStyles(() => ({
  error: {
    fontSize: 12,
    fontWeight: 'bold',
    color: Color.WHITE,
    background: Color.ERROR,
    padding: '5px 10px',
  },
}));

const newDefaultPolicy = {
  'Version': '2012-10-17',
  'Statement': [],
};

const PutPolicyDialog = ({policy, policies=[], onSubmit, ...others}: Props) => {
  const classes = useStyles();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [error, setError] = useState('');

  const defaultPolicy = (policy && policy.document) ? policy.document : newDefaultPolicy;
  const defaultPolicyString = JSON.stringify(defaultPolicy, null, 2);
  const [policyString, setPolicyString] = useState(defaultPolicyString);

  const initialValues = policy ? policy : {document: defaultPolicy};

  // parsePolicy parses the policy JSON string. It returns one of the
  // following:
  //  false: It's invalid JSON
  //  null: It's an empty policy (e.g. the user is removing a policy).
  //        It's technically not valid JSON, however, it is a valid
  //        policy.
  //  everything else: The parsed JSON policy (e.g. a JSON object).
  const parsePolicy = (value: string) => {
    let policy: any = null;
    if (value !== '') {
      try {
        policy = JSON.parse(value);
      } catch {
        policy = false;
      }
    }
    return policy;
  };

  const title = policy ? 'Edit IAM Group Policy' : 'Create IAM Group Policy';

  const validate = ({name}: FormData) => {
    const errors = {} as any;
    errors.name = validateName(name);
    if (!errors.name) {
      const lowerName = name.toLowerCase();
      const existingPolicy = policies.find((p) => {
        return p.name.toLowerCase() === lowerName;
      });
      if (existingPolicy && !policy) {
        errors.name = 'Policy name must be unique';
      }
    }
    return errors;
  };

  return (
    <FormDialog title={title} disableSubmit={disableSubmit} initialValues={initialValues} validate={validate}
      onSubmit={onSubmit} {...others}>
      {({form}: FormRenderProps) => {
        const onChange = (value: string) => {
          setPolicyString(value);
          const policy = parsePolicy(value);
          setDisableSubmit(policy === false);
          if (policy !== false) {
            setError('');
            if (policy !== undefined && value !== defaultPolicyString) {
              form.mutators.setFormValue('document', policy);
            }
          } else {
            setError('Badly formatted JSON');
          }
        };

        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField name='name' label='Policy Name' disabled={!!policy} />
            </Grid>
            <Grid item xs={12}>
              {error && <Typography className={classes.error}>{error}</Typography>}
              <AceEditor
                value={policyString} mode='json' theme='github' placeholder='Create Policy' fontSize={12}
                wrapEnabled={true} showPrintMargin={false} height='305px' width='100%' onChange={onChange}
                setOptions={{tabSize: 2, useWorker: false}} editorProps={{$blockScrolling: true}} />
            </Grid>
          </Grid>
        );
      }}
    </FormDialog>
  );
};

const mapDispatchToProps = (dispatch: Dispatch, {group}: Props) => {
  const path = `iam/groups/${group.accountid}/${group.name}/policies`;
  return {
    onSubmit: ({name, document}: FormData) => dispatch(updateResource(path, name, document || {})),
  };
};

export default connect(null, mapDispatchToProps)(PutPolicyDialog);
